/*
  globe.js — draaibare wereldbol voor de sectie "Wereldwijd gedragen".

  Afgeleid van bron/elpasha-globe.js uit de handoff, met drie wijzigingen die
  nodig zijn in een thema:

  1. De verhalen komen uit de sectieblokken (een JSON-script in de sectie),
     niet uit een array in dit bestand.
  2. d3, topojson en de kaartgegevens (samen ±100 kB) worden pas geladen
     wanneer de sectie in beeld komt, niet bij het laden van de pagina.
  3. Alles wordt binnen de sectie opgezocht, zodat de bol twee keer op een
     pagina kan staan zonder dat de panelen elkaar overschrijven.

  Bij prefers-reduced-motion draait de bol niet vanzelf.
*/

(function () {
  'use strict';

  /*
    De adressen worden door de sectie meegegeven, want alleen Liquid kent het
    adres van een themabestand. Staan ze er niet, dan gebeurt er niets — beter
    een bol die uitblijft dan een die van een vreemde partij laadt.
  */
  var scriptNode = document.currentScript;
  var D3_SRC = scriptNode ? scriptNode.getAttribute('data-d3') : '';
  var KAART_SRC = scriptNode ? scriptNode.getAttribute('data-kaart') : '';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var loading = null;

  /*
    Landnaam naar numerieke ISO 3166-1 code, zodat de eigenaar alleen "Marokko"
    hoeft in te vullen. Nederlands en Engels staan er allebei in. Staat een land
    er niet bij, dan kan de code in het blok handmatig worden ingevuld.
  */
  var COUNTRY_CODES = {
    nederland: 528, belgie: 56, duitsland: 276, frankrijk: 250, verenigdkoninkrijk: 826,
    spanje: 724, italie: 380, portugal: 620, oostenrijk: 40, zwitserland: 756,
    zweden: 752, noorwegen: 578, denemarken: 208, finland: 246, polen: 616,
    tsjechie: 203, turkije: 792, marokko: 504, bosnie: 70, bosnieenherzegovina: 70,
    kroatie: 191, servie: 688, griekenland: 300, verenigdestaten: 840, canada: 124,
    verenigdearabischeemiraten: 784, saoediarabie: 682, egypte: 818, suriname: 740,
    indonesie: 360, australie: 36, ierland: 372, luxemburg: 442, roemenie: 642,
    bulgarije: 100, hongarije: 348, albanie: 8, noordmacedonie: 807, montenegro: 499,
    slovenie: 705, slowakije: 703, oekraine: 804, rusland: 643, israel: 376,
    libanon: 422, jordanie: 400, qatar: 634, koeweit: 414, tunesie: 788,
    algerije: 12, zuidafrika: 710, nieuwzeeland: 554, japan: 392, china: 156,
    india: 356, brazilie: 76, mexico: 484, argentinie: 32, costarica: 188,
    amerika: 840, vs: 840, panama: 591, colombia: 170, chili: 152, peru: 604,
    thailand: 764, vietnam: 704, filipijnen: 608, maleisie: 458, singapore: 702,
    zuidkorea: 410, taiwan: 158, pakistan: 586, bangladesh: 50, nigeria: 566,
    kenia: 404, ghana: 288, senegal: 686, ivoorkust: 384, curacao: 531,
    aruba: 533, ijsland: 352, estland: 233, letland: 428, litouwen: 440,
    cyprus: 196, malta: 470, monaco: 492,

    netherlands: 528, belgium: 56, germany: 276, france: 250, unitedkingdom: 826,
    greatbritain: 826, spain: 724, italy: 380, austria: 40, switzerland: 756,
    sweden: 752, norway: 578, denmark: 208, poland: 616, czechia: 203,
    czechrepublic: 203, turkey: 792, turkiye: 792, morocco: 504,
    bosniaandherzegovina: 70, croatia: 191, serbia: 688, greece: 300,
    unitedstates: 840, unitedstatesofamerica: 840, usa: 840,
    unitedarabemirates: 784, saudiarabia: 682, egypt: 818, indonesia: 360,
    australia: 36, ireland: 372, luxembourg: 442, romania: 642, bulgaria: 100,
    hungary: 348, albania: 8, northmacedonia: 807, slovenia: 705, slovakia: 703,
    ukraine: 804, russia: 643, lebanon: 422, jordan: 400, kuwait: 414,
    tunisia: 788, algeria: 12, southafrica: 710, newzealand: 554, brazil: 76,
    argentina: 32, chile: 152, philippines: 608, malaysia: 458,
    southkorea: 410, korea: 410, kenya: 404, ivorycoast: 384, iceland: 352,
    estonia: 233, latvia: 428, lithuania: 440
  };

  function normalizeName(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z]/g, '');
  }

  function lookupCountryCode(name) {
    var key = normalizeName(name);
    if (!key) return '';
    if (COUNTRY_CODES[key]) return String(COUNTRY_CODES[key]);

    /* "Bosnie en Herzegovina" tegenover "Bosnie": val terug op een deelmatch. */
    var keys = Object.keys(COUNTRY_CODES);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i].indexOf(key) === 0 || key.indexOf(keys[i]) === 0) {
        return String(COUNTRY_CODES[keys[i]]);
      }
    }
    return '';
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded) return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.addEventListener('load', function () {
        script.dataset.loaded = '1';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function loadLibraries() {
    if (loading) return loading;
    if (!D3_SRC || !KAART_SRC) return Promise.reject(new Error('geen bronnen'));
    loading = loadScript(D3_SRC).then(function () {
      return loadScript(KAART_SRC);
    });
    return loading;
  }

  /*
    Het midden van een land.

    Niet het zwaartepunt van alle losse stukken samen: bij Frankrijk telt Frans-
    Guyana dan mee en komt het punt in de Atlantische Oceaan te liggen, bij de
    Verenigde Staten trekt Alaska het naar het noorden en bij Indonesie valt het
    tussen twee eilanden in zee. Daarom het midden van het grootste stuk -- dat
    is het vasteland, en daar hoort de speld.
  */
  function middenVan(feature) {
    if (!feature || !feature.geometry) return null;

    if (feature.geometry.type !== 'MultiPolygon') return d3.geoCentroid(feature);

    var grootste = null;
    var grootsteOpp = -1;

    feature.geometry.coordinates.forEach(function (ringen) {
      var stuk = { type: 'Polygon', coordinates: ringen };
      var opp = d3.geoArea(stuk);
      if (opp > grootsteOpp) {
        grootsteOpp = opp;
        grootste = stuk;
      }
    });

    return d3.geoCentroid(grootste || feature);
  }

  function build(section) {
    var wrap = section.querySelector('[data-globe-canvas]');
    var dataNode = section.querySelector('[data-globe-data]');
    if (!wrap || !dataNode || wrap.dataset.ready) return;

    var stories;
    try {
      stories = JSON.parse(dataNode.textContent);
    } catch (e) {
      return;
    }
    if (!stories.length) return;

    wrap.dataset.ready = '1';

    var css = getComputedStyle(section);
    var token = function (name, fallback) {
      return (css.getPropertyValue(name) || '').trim() || fallback;
    };

    var ONYX = token('--onyx', '#0E0C0A');
    var ZEE = '#12100E';
    var LAND = '#241C15';
    var ACTIEF = '#4A3826';
    var LIJN = token('--hairline', '#2A1E17');
    var CHAMP = token('--champagne', '#E7CE8A');
    var IVOOR = token('--ivoor', '#EFE7D6');

    var SIZE = 520;
    var RADIUS = 224;

    var svg = d3.select(wrap).append('svg')
      .attr('class', 'globe__svg')
      .attr('viewBox', '0 0 ' + SIZE + ' ' + SIZE)
      .attr('role', 'img')
      .attr('aria-label', wrap.dataset.globeCanvas || 'Wereldbol');

    var defs = svg.append('defs');
    var glowId = 'globe-glow-' + section.id;
    var glow = defs.append('radialGradient').attr('id', glowId)
      .attr('cx', '50%').attr('cy', '50%').attr('r', '50%');
    glow.append('stop').attr('offset', '70%').attr('stop-color', token('--goud', '#9C7B54')).attr('stop-opacity', '0');
    glow.append('stop').attr('offset', '100%').attr('stop-color', token('--goud', '#9C7B54')).attr('stop-opacity', '.14');

    var projection = d3.geoOrthographic().scale(RADIUS).translate([SIZE / 2, SIZE / 2]).clipAngle(90).rotate([-10, -20]);
    var path = d3.geoPath(projection);

    svg.append('circle').attr('cx', SIZE / 2).attr('cy', SIZE / 2).attr('r', RADIUS)
      .attr('fill', ZEE).attr('stroke', '#241C16').attr('stroke-width', 1);

    var gLand = svg.append('g');
    var gPins = svg.append('g');

    svg.append('circle').attr('cx', SIZE / 2).attr('cy', SIZE / 2).attr('r', RADIUS)
      .attr('fill', 'url(#' + glowId + ')').attr('pointer-events', 'none');

    var autoRotate = !reduceMotion;
    var lastInteraction = 0;

    var panelQuote = section.querySelector('[data-globe-quote]');
    var panelSource = section.querySelector('[data-globe-source]');
    var chipHolder = section.querySelector('[data-globe-chips]');
    var landHolder = section.querySelector('[data-globe-landen]');
    var landNaam = section.querySelector('[data-globe-landnaam]');
    var stadTerug = section.querySelector('[data-globe-stadterug]');
    var stadVerder = section.querySelector('[data-globe-stadverder]');
    var beeld = section.querySelector('[data-globe-beeld]');
    var leegvak = section.querySelector('[data-globe-leeg]');
    var mediavak = section.querySelector('[data-globe-media]');

    /*
      Overal dezelfde schrijfwijze van een landnaam.

      Een spatie achter "Belgie " in de winkel was genoeg om de hele bol te
      laten vallen: de lijst met landen werd wel getrimd, het opzoeken van de
      bijbehorende verhalen niet, en dan bleef er een land over zonder verhaal.
      Vandaar dat alles door deze ene functie gaat.
    */
    function landVan(story) {
      return String((story && story.land) || '').trim();
    }

    /* De landen in de volgorde waarin ze in de verhalen staan, zonder dubbele. */
    var landen = [];
    stories.forEach(function (story) {
      var naam = landVan(story);
      if (naam && landen.indexOf(naam) === -1) landen.push(naam);
    });
    var landIndex = 0;

    /*
      Alleen de naam en de zichtbare plaatsen bijwerken. Dit draait ook wanneer
      iemand een speld op de bol aantikt, dus hier mag niets staan dat opnieuw
      een verhaal opent -- dat zou zichzelf blijven aanroepen.
    */
    function toonLand(naam) {
      naam = String(naam || '').trim();

      var i = landen.indexOf(naam);
      if (i === -1) return;
      landIndex = i;
      if (landNaam) landNaam.textContent = naam;

      if (landHolder) {
        Array.prototype.forEach.call(landHolder.children, function (knop) {
          knop.classList.toggle('is-active', knop.dataset.globeLand === naam);
        });
      }

      if (!chipHolder) return;
      Array.prototype.forEach.call(chipHolder.children, function (chip) {
        chip.hidden = String(chip.dataset.land || '').trim() !== naam;
      });
    }

    /* De plaatsen van het land dat nu gekozen is, in de volgorde van de rij. */
    function zichtbareChips() {
      if (!chipHolder) return [];
      return Array.prototype.filter.call(chipHolder.children, function (chip) {
        return !chip.hidden;
      });
    }

    /*
      De pijltjes staan uit aan het begin en het eind, en verdwijnen helemaal
      bij een land met één plaats -- een knop die niets doet is erger dan geen
      knop.
    */
    function ververStadKnoppen() {
      if (!stadTerug && !stadVerder) return;
      var lijst = zichtbareChips();
      var nu = -1;
      for (var i = 0; i < lijst.length; i++) {
        if (lijst[i].classList.contains('is-active')) nu = i;
      }

      var toon = lijst.length > 1;
      if (stadTerug) {
        stadTerug.hidden = !toon;
        stadTerug.disabled = nu <= 0;
      }
      if (stadVerder) {
        stadVerder.hidden = !toon;
        stadVerder.disabled = nu === -1 || nu >= lijst.length - 1;
      }
    }

    function stapStad(richting) {
      var lijst = zichtbareChips();
      var nu = -1;
      for (var i = 0; i < lijst.length; i++) {
        if (lijst[i].classList.contains('is-active')) nu = i;
      }

      var volgende = lijst[nu + richting];
      if (!volgende) return;

      var story = stories.filter(function (s) { return s.id === volgende.dataset.storyId; })[0];
      if (story) show(story);
    }

    /* De actieve plaats in beeld schuiven, ook als de rij verder doorloopt. */
    function haalChipInBeeld(chip) {
      if (!chipHolder || !chip || chip.hidden) return;
      var c = chip.getBoundingClientRect();
      var h = chipHolder.getBoundingClientRect();
      if (c.left < h.left) chipHolder.scrollLeft += c.left - h.left - 20;
      else if (c.right > h.right) chipHolder.scrollLeft += c.right - h.right + 20;
    }

    function visible(story) {
      var r = projection.rotate();
      return d3.geoDistance([story.lon, story.lat], [-r[0], -r[1]]) < Math.PI / 2;
    }

    function draw() {
      gLand.selectAll('path').attr('d', path);
      gPins.selectAll('g.globe__pin').each(function (d) {
        d3.select(this)
          .attr('transform', 'translate(' + projection([d.lon, d.lat]) + ')')
          .attr('opacity', visible(d) ? 1 : 0);
      });
    }

    function show(story) {
      if (panelQuote) panelQuote.textContent = story.quote;
      if (panelSource) panelSource.textContent = story.bron;

      /*
        Het beeld wordt nu pas opgehaald. Stonden alle foto's vooraf in de
        pagina, dan kostte een archief van honderd plaatsen honderd verzoeken
        bij het laden -- ook al ziet de bezoeker er maar één.
      */
      /*
        Geen foto bij dit verhaal betekent geen beeldvak. Een leeg vlak met een
        tekstje erin is geen plaatsvervanger voor een foto -- het is een gat.
      */
      if (mediavak) mediavak.hidden = !story.beeld;

      if (beeld) {
        if (story.beeld) {
          if (beeld.getAttribute('src') !== story.beeld) beeld.src = story.beeld;
          beeld.alt = story.stad || '';
          beeld.hidden = false;
          beeld.classList.add('is-active');
          if (leegvak) leegvak.hidden = true;
        } else {
          beeld.hidden = true;
          beeld.classList.remove('is-active');
          beeld.removeAttribute('src');
          if (leegvak) leegvak.hidden = false;
        }
      }

      /* Eén punt per land, dus het punt van het land van dit verhaal licht op. */
      var ditLand = landVan(story);
      gPins.selectAll('circle.globe__pin-core')
        .attr('fill', function (d) { return d.naam === ditLand ? IVOOR : CHAMP; })
        .attr('r', function (d) { return d.naam === ditLand ? 6 : 4.5; });

      toonLand(landVan(story));

      if (chipHolder) {
        Array.prototype.forEach.call(chipHolder.children, function (chip) {
          var raak = chip.dataset.storyId === story.id;
          chip.classList.toggle('is-active', raak);
          if (raak) haalChipInBeeld(chip);
        });
      }

      ververStadKnoppen();
    }

    /*
      Draaien gebeurt per land, niet per plaats: de punten staan op het land,
      en twee steden in hetzelfde land liggen op deze schaal bovenop elkaar.
      Zo schokt de bol niet bij elke tik op een plaatsnaam.
    */
    function draaiNaarLand(punt) {
      if (!punt) return;
      autoRotate = false;
      if (reduceMotion) {
        projection.rotate([-punt.lon, -punt.lat + 8]);
        draw();
      } else {
        d3.transition().duration(1100).tween('globe-rotate', function () {
          var i = d3.interpolate(projection.rotate(), [-punt.lon, -punt.lat + 8]);
          return function (t) { projection.rotate(i(t)); draw(); };
        }).on('end', function () {
          lastInteraction = Date.now();
          autoRotate = !reduceMotion;
        });
      }
    }

    /*
      De kaart staat al in het geheugen; wereldkaart.js zet hem daar neer.
      Geen verzoek meer dat kan mislukken tussen ophalen en tekenen.
    */
    Promise.resolve(window.__elPashaWereldkaart).then(function (topology) {
      if (!topology) return;
      var features = topojson.feature(topology, topology.objects.countries).features;

      /*
        De kaartgegevens bewaren de landcode als drie tekens met nullen ervoor:
        "056" voor Belgie, "070" voor Bosnie. De lijst hierboven levert "56" en
        "70". Daardoor vond elk land met een code onder de honderd zichzelf niet
        op de kaart, en belandde de speld op 0 graden -- midden in de Golf van
        Guinee. Vandaar beide schrijfwijzen als sleutel.
      */
      var byCode = {};
      var byNaam = {};
      features.forEach(function (feature) {
        var id = String(feature.id);
        byCode[id] = feature;

        var kaal = parseInt(id, 10);
        if (isFinite(kaal)) byCode[String(kaal)] = feature;

        /*
          De kaart draagt zijn eigen landnamen bij zich. Dat is een tweede
          ingang naast de lijst hierboven: Costa Rica staat wel op de kaart maar
          stond niet in die lijst, en dan bleef de speld weg. Zo werkt elk land
          op de kaart, ook zonder dat iemand het eerst toevoegt.
        */
        var naam = normalizeName(feature.properties && feature.properties.name);
        if (naam) byNaam[naam] = feature;
      });

      /*
        Per verhaal: de landcode uit de naam afleiden, en de speld midden op dat
        land zetten. Staan er wel coordinaten in het blok, dan gaan die voor.
      */
      stories.forEach(function (story) {
        var code = String(story.country_code || '').trim() || lookupCountryCode(story.land);
        story.code = code;

        var feature = byCode[code] || byNaam[normalizeName(story.land)];
        var centroid = middenVan(feature);

        var lon = parseFloat(story.lon);
        var lat = parseFloat(story.lat);

        story.lon = isFinite(lon) ? lon : (centroid ? centroid[0] : 0);
        story.lat = isFinite(lat) ? lat : (centroid ? centroid[1] : 0);

        /*
          Zonder land op de kaart en zonder eigen coordinaten is er geen plek
          bekend. Dan liever geen speld dan een speld in zee: het verhaal blijft
          gewoon in de rij staan en de eigenaar leest in de console wat eraan
          ontbreekt.
        */
        story.geplaatst = !!centroid || (isFinite(lon) && isFinite(lat));

        if (!feature && window.console) {
          console.warn('[El Pasha] Land niet gevonden op de kaart: ' + story.land +
            '. Vul de lengte- en breedtegraad in bij dit verhaal, of de landcode.');
        }
      });

      var activeIds = stories.map(function (s) { return s.code; });

      gLand.selectAll('path').data(features).join('path')
        .attr('fill', function (d) {
          return activeIds.indexOf(String(d.id)) !== -1 ? ACTIEF : LAND;
        })
        .attr('stroke', 'none');

      /*
        Eén punt per land. Bij honderd plaatsen zouden honderd spelden op
        Nederland een goudkleurige vlek worden; de plaatsen staan in de rij
        onder het beeld.
      */
      var landPunten = landen.map(function (naam) {
        var eigen = stories.filter(function (s) { return landVan(s) === naam; });
        if (!eigen.length) return null;

        var feature = byCode[eigen[0].code] || byNaam[normalizeName(naam)];
        var centroid = middenVan(feature);

        return {
          naam: naam,
          lon: centroid ? centroid[0] : eigen[0].lon,
          lat: centroid ? centroid[1] : eigen[0].lat,
          plaatsen: eigen.length,
          geplaatst: !!centroid || eigen[0].geplaatst
        };
      }).filter(function (punt) { return punt && punt.geplaatst; });

      function puntVanLand(naam) {
        return landPunten.filter(function (p) { return p.naam === naam; })[0];
      }

      var pins = gPins.selectAll('g.globe__pin').data(landPunten).join('g')
        .attr('class', 'globe__pin').attr('pointer-events', 'none');
      pins.append('circle').attr('class', 'globe__pin-core').attr('r', 4.5)
        .attr('fill', CHAMP).attr('stroke', ONYX).attr('stroke-width', 1.2);
      pins.append('title').text(function (d) { return d.naam; });

      /* Een plaats kiezen laat de bol staan: het land is al in beeld. */
      if (chipHolder) {
        Array.prototype.forEach.call(chipHolder.children, function (chip) {
          chip.addEventListener('click', function () {
            var story = stories.filter(function (s) { return s.id === chip.dataset.storyId; })[0];
            if (story) show(story);
          });
        });
      }

      /* Een land kiezen draait de bol en opent de eerste plaats van dat land. */
      function kiesLand(naam) {
        /* De naam komt uit een knop in de pagina; schoonmaken voor gebruik. */
        naam = String(naam || '').trim();

        var eerste = stories.filter(function (s) { return landVan(s) === naam; })[0];
        if (!eerste) return;
        toonLand(naam);
        if (chipHolder) chipHolder.scrollLeft = 0;
        draaiNaarLand(puntVanLand(naam));
        show(eerste);
      }

      if (landHolder) {
        Array.prototype.forEach.call(landHolder.children, function (knop) {
          knop.addEventListener('click', function () { kiesLand(knop.dataset.globeLand); });
        });
      }

      if (stadTerug) stadTerug.addEventListener('click', function () { stapStad(-1); });
      if (stadVerder) stadVerder.addEventListener('click', function () { stapStad(1); });

      var node = svg.node();
      var start = null;
      var startRotation = null;

      svg.call(d3.drag()
        .on('start', function (e) {
          autoRotate = false;
          start = [e.x, e.y];
          startRotation = projection.rotate();
          node.classList.add('is-dragging');
        })
        .on('drag', function (e) {
          projection.rotate([
            startRotation[0] + (e.x - start[0]) * 0.3,
            Math.max(-80, Math.min(80, startRotation[1] - (e.y - start[1]) * 0.3)),
            startRotation[2]
          ]);
          draw();
        })
        .on('end', function () {
          node.classList.remove('is-dragging');
          lastInteraction = Date.now();
          autoRotate = !reduceMotion;
        }));

      node.addEventListener('click', function (e) {
        var point = d3.pointer(e, node);
        var coord = projection.invert(point);
        if (!coord) return;

        var best = null;
        var bestDistance = Infinity;
        landPunten.forEach(function (punt) {
          if (!visible(punt)) return;
          var distance = d3.geoDistance([punt.lon, punt.lat], coord);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = punt;
          }
        });
        if (best && bestDistance < 0.3) kiesLand(best.naam);
      });

      /*
        Zoomen zit er bewust niet in: de kaartgegevens zijn grof, dus verder
        inzoomen levert blokkerige kustlijnen op, en op een telefoon vocht het
        knijpen met het draaien. De bol houdt één vaste grootte.
      */

      if (!reduceMotion) {
        d3.timer(function () {
          if (!autoRotate || Date.now() - lastInteraction < 2500) return;
          var r = projection.rotate();
          projection.rotate([r[0] + 0.12, r[1], r[2]]);
          draw();
        });
      }

      draw();
      show(stories[0]);
      if (!reduceMotion) draaiNaarLand(puntVanLand(stories[0].land));
    }).catch(function () {
      // Kaartgegevens niet bereikbaar: laat de sectie staan zonder bol.
      wrap.classList.add('globe__canvas--failed');
    });
  }

  /* Pas laden wanneer de sectie in beeld komt. */
  document.querySelectorAll('[data-globe]').forEach(function (section) {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        loadLibraries().then(function () { build(section); }).catch(function () {});
      });
    }, { rootMargin: '200px' });

    observer.observe(section);
  });
})();
